import React from 'react';
import { Link } from 'react-router';
import { injectIntl, FormattedMessage } from 'react-intl';

import ValidationIcons from './../../ValidationIcons/ValidationIcons';

import utils from './../../../utils/utilities';
var dateFormat = require('dateformat');

/**
 * Creates an empty table cell. The cell receives content via i18n.
 *
 * @constructor
 * @param   {number} columnWidth          The width of the column as percentage.
 * @param   {string} emptyCellMessageId   The dictionary key for the empty cell message.
 * @return  {HTML}                        The empty table cell DOM elements.
 */
export function EmptyCell({columnWidth, emptyCellMessageId}) {

    return (<td style={{width: columnWidth + '%'}} className="custom-table-body__empty-cell">
        {typeof emptyCellMessageId === 'string' ? <FormattedMessage id={emptyCellMessageId} /> : <span>&nbsp;</span>}
    </td>);

}

/**
 * Creates a generic table cell with various value formatting options.
 *
 * @constructor
 * @param   {*}               value         The value to be rendered in the table cell.
 * @param   {string|function} format        The formatting pattern string or function
 *                                            which will be applied on the value.
 * @param   {string}          classes       The classes to be added to the table cell.
 * @param   {number}          columnWidth   The width of the column as percentage.
 * @param   {CustomTableBody} table         The reference to the table body component.
 * @return  {HTML}                          The table cell DOM elements.
 */
export function Cell({value, format, classes, columnWidth, table, highlight}) {

    classes = classes ? classes : null;

    // FORMAT THE CELL VALUE

    // The format property is a formatting string (%v is replaced with the cell value. HTML text formatting
    // tags are allowed: 'b', 'em', 'i', 'small', 'strong', 'sub', 'sup', 'ins', 'del', 'mark').
    if (typeof format === 'string') {

        value = utils.parseHTMLFormattingString(format, value);

        if (value === '') {

            return (<EmptyCell emptyCellMessageId={table.state.emptyCellMessageId} columnWidth={columnWidth} />);

        } else {

            value = {__html: value};
            return (<td style={{width: columnWidth + '%'}}
                        className={classes}
                        dangerouslySetInnerHTML={value}></td>);

        }

        // The format property is a formatting function: format(value, tableData, row, column)
    } else if (typeof format === 'function') {

        value = format();

        if (!React.isValidElement(value)) {
            return (<EmptyCell emptyCellMessageId={table.state.emptyCellMessageId} columnWidth={columnWidth} />);
        }

        // No format property is specified, case in which default formatting is applied
    } else {

        if (utils.isArray(value)) {

            value = value.join(', ');

        } else if (utils.isObject(value)) {

            let newValue = [], keys = value.keys();

            for (let i = 0; i < keys.length; i++) {
                newValue.push(
                    <span className="custom-table-body__column-key-value">
                        {keys[i] + ': ' + value[keys[i]] + (i === keys.length - 1 ? '' : ',')}
                    </span>
                );
            }

            value = newValue;

        } else {

            if (!value) {
                return (<EmptyCell emptyCellMessageId={table.state.emptyCellMessageId} columnWidth={columnWidth} />);
            }
            value = value.toString();

        }

    }

    if (highlight) {
        let val = utils.highlightSubStrings(highlight, value);
        val = {__html: val};
        return (<td style={{width: columnWidth + '%'}}
                    className={classes}
                    title={ typeof value !== 'object' ? value : null }
                    dangerouslySetInnerHTML={val}></td>);
    }

    // Render the cell
    return (<td className={classes} title={typeof value !== 'object' ? value : null} style={{width: columnWidth + '%'}}>{value}</td>);

}

/**
 * Cell which contains a boolean value (e.g.: yes/no, true/false, available/not available, etc.).
 *
 * @constructor
 * @param   {*}                value           The value to be rendered in the table cell.
 * @param   {string|function}  format          The formatting pattern string or function which
 *                                             will be applied on the value.
 * @param   {string}           classes         The classes to be added to the table cell.
 * @param   {string}           booleanValues   String with formatting options to replace true/false boolean values with
 *                                             the ones specified by the following format:
 *                                             "Your true case text|Your false case text".
 * @param   {number}           columnWidth     The width of the column as percentage.
 * @return  {HTML}                             The table cell DOM elements.
 */
export function BooleanCell({value, format, classes, booleanValues, columnWidth}) {

    // Replace "true" and "false" values with the specified boolean values but ONLY IF THE FORMAT FUNCTION IS
    // NOT SPECIFIED. You CANNOT have at the same time the booleanValues array and the format function!
    if (typeof(format) !== 'function') {
        value = value ? booleanValues[0] : booleanValues[1];
    }

    // Return the cell component
    return (
        <Cell value={value}
              format={format}
              classes={classes}
              columnWidth={columnWidth} />
    );

}

/**
 * Cell containing an e-mail address.
 *
 * @constructor
 * @param   {*}      value                The value to be rendered in the table cell.
 * @param   {string} classes              The classes to be added to the table cell.
 * @param   {number} columnWidth          The width of the column as percentage.
 * @return  {HTML}                        The table cell DOM elements.
 */
export function EmailCell({value, classes, columnWidth}) {

    return (
        <td className={classes} style={{width: columnWidth + '%'}}>
            <a class="user-email" href={'mailto:' + value}>{value}</a>
        </td>
    );

}

/**
 * Cell that contains a link.
 *
 * @constructor
 * @param   {*}      value         The value to be rendered in the table cell.
 * @param   {string} classes       The classes to be added to the table cell.
 * @param   {number} columnWidth   The width of the column as percentage.
 * @return  {HTML}                 The table cell DOM elements.
 */
function LinkCell({classes, value, columnWidth, display, relativePath, isReactRouterLink, openInNewWindow, intl}) {

    let fullPath     = relativePath ? (relativePath + '/' + value) : value,
        displayValue = display ? display : fullPath,
        displayValueI18n = intl.formatMessage({id: displayValue.props.id});

    return (
        <td className={classes} style={{width: columnWidth + '%'}}>
            {isReactRouterLink ?
                <Link to={fullPath} title={displayValueI18n}>{displayValue}</Link>
                :
                <a target={openInNewWindow ? '_blank' : '_self'} href={fullPath}>{displayValue}</a>
            }
        </td>
    );

}
LinkCell = injectIntl(LinkCell);
export { LinkCell };

export function ButtonLinkCell({classes, value, columnWidth, display, relativePath, isReactRouterLink, openInNewWindow, buttonClass}) {

    let fullPath     = relativePath ? (relativePath + '/' + value) : value,
        displayValue = display ? display : fullPath;

    return (
        <td className={classes} style={{width: columnWidth + '%'}}>
            {isReactRouterLink ?
                <Link to={fullPath}>
                    <button className={buttonClass}>{displayValue}</button>
                </Link>
                :
                <a target={openInNewWindow ? '_blank' : '_self'} href={fullPath}>
                    <button className={buttonClass}>{displayValue}</button>
                </a>
            }
        </td>
    );

}

/**
 * Cell that contains an image.
 *
 * @constructor
 * @param   {*}               value         The value to be rendered in the table cell.
 * @param   {string}          classes       The classes to be added to the table cell.
 * @param   {string|function} altText       The text to be added to the image "alt" attribute.
 * @param   {number}          columnWidth   The width of the column as percentage.
 * @return  {HTML}                          The table cell DOM elements.
 */
export function ImageCell({value, classes, altText, columnWidth}) {

    altText = typeof altText === 'function' ? altText() : altText;
    altText = typeof altText === 'string' ? altText : value.toString();

    return (<td className={classes} style={{width: columnWidth + '%'}}><img alt={altText} src={value} /></td>);

}

/**
 * Cell that contains an array of translated messages.
 *
 * @constructor
 * @param  {*}               value         The value to be rendered in the table cell.
 * @param  {string}          classes       The classes to be added to the table cell.
 * @param  {number}          columnWidth   The width of the column as percentage.
 * @param  {CustomTableBody} table         The reference to the table body component.
 * @return {HTML}                          The table cell DOM elements.
 */
export function MessageCodesCell({value, classes, columnWidth, table}) {

    let messageCodes = 'General_CoreFunctionality_UIText_general.' + value,
        messages     = [];

    if (utils.isArray(value)) {
        for (let i = 0; i < value.length; i++) {
            if (typeof messageCodes[i] === 'string') {
                messages.push(<FormattedMessage key={'row-messages-' + i} id={'General_CoreFunctionality_UIText_general.' + value[i]} />);
            }
        }
    }

    if (messages.length === 0) {
        return (<EmptyCell emptyCellMessageId={table.state.emptyCellMessageId} columnWidth={columnWidth} />);
    }

    return (
        <td className={classes} style={{width: columnWidth + '%'}}>
            <span className='row-message-codes'>{messages}</span>
        </td>
    );

}

/**
 * Cell that contains labels.
 *
 * @constructor
 * @param  {Array}           value           The label keys array.
 * @param  {Array}           labels          The array with all the currently available label texts.
 * @param  {string}          classes         The classes to be added to the table cell.
 * @param  {number}          columnWidth     The width of the column as percentage.
 * @param  {CustomTableBody} table           The reference to the table body component.
 * @param  {boolean}         noCloseButton   The reference to the table body component.
 * @return {HTML}                            The table cell DOM elements.
 */
export function LabelsCell({value, labels, classes, columnWidth, table, rowData, noCloseButton}) {

    let labelKeys      = value,
        renderedLabels = [];

    if (utils.isArray(labelKeys) && utils.isObject(labels) && Object.keys(labels).length > 0) {
        for (let i = 0; i < labelKeys.length; i++) {
            if (typeof labelKeys[i] === 'string' && typeof labels[labelKeys[i]] === 'string') {
                renderedLabels.push(
                    <span key={'tags-label-' + i}
                          id={'tags-label-' + i}
                          title={labels[labelKeys[i]]}
                          className={'tags-label notranslate ' + (noCloseButton ? 'no-close-button': '')}>
                        {labels[labelKeys[i]]}
                        {noCloseButton ?
                            null : <span className="tags-label__close-button la-CloseRemove"
                                         data-category-id={labelKeys[i]}
                                         data-company-id={rowData.id}>
                                   </span>}
                    </span>
                );
            }
        }
    }

    if (renderedLabels.length === 0) {
        return (<EmptyCell emptyCellMessageId={table.state.emptyCellMessageId} columnWidth={columnWidth} />);
    }

    return (
        <td className={classes} style={{width: columnWidth + '%'}}>
            {renderedLabels}
        </td>
    );

}

/**
 * Cell that contains Concatenated strings.
 *
 * @constructor
 * @param  {Array}           value           The label keys array.
 * @param  {Array}           labels          The array with all the currently available label texts.
 * @param  {string}          classes         The classes to be added to the table cell.
 * @param  {number}          columnWidth     The width of the column as percentage.
 * @param  {CustomTableBody} table           The reference to the table body component.
 * @param  {boolean}         noCloseButton   The reference to the table body component.
 * @return {HTML}                            The table cell DOM elements.
 */
export function SimpleArrayCell({value, labels, classes, columnWidth, table, rowData, noCloseButton, highlight}) {

    let renderedText = '';
    if (value) {
        renderedText = value.join(', ');
    }

    if (highlight) {
        let val = utils.highlightSubStrings(highlight, renderedText);
        val = {__html: val};
        return (<td style={{width: columnWidth + '%'}}
                    className={classes}
                    dangerouslySetInnerHTML={val}></td>);
    }

    return (
        <td className={classes} style={{width: columnWidth + '%'}}>
            {renderedText}
        </td>
    );

}

/**
 * Cell that contains a checkbox.
 *
 * @constructor
 * @param  {number}          props.value              The ID of the current checkbox.
 * @param  {function}        props.getSelectedItems   Returns the array of IDs that are currently selected.
 * @param  {function}        props.isAllSelected      Returns true if all items are selected or false otherwise.
 * @param  {string}          props.checkboxPrefix     The prefix which will be prepended to the
 *                                                        checkbox ID in order to form the checkbox name.
 * @param  {string}          props.propertyToCheck    Object property to check in the selected items array.
 * @param  {string}          props.classes            The classes to be added to the table cell.
 * @param  {number}          props.columnWidth        The width of the column as percentage.
 * @param  {number}          props.row                The index of the row containing this cell.
 * @return {HTML}                                     The table cell DOM elements.
 */
export function CheckboxCell(props) {

    let checkboxId = props.value,
        items      = props.getSelectedItems(),
        checked    = props.isAllSelected() || (utils.isArray(items) &&
        utils.inArray(checkboxId, items, props.propertyToCheck) > -1) ? true : false;

    return (
        <td className={props.classes} style={{width: props.columnWidth + '%'}}>
            <input data-checkbox-id={checkboxId}
                   data-checkbox-index={props.row}
                   name={props.checkboxPrefix ? props.checkboxPrefix + '-' + checkboxId : 'checkbox-' + checkboxId}
                   type="checkbox"
                   defaultChecked={checked}
                   data-checkbox-checked={checked}
                   id={'checkbox-' + checkboxId} />
        </td>
    );

}


/**
 * Cell that contains specific formatted date.
 *
 * @constructor
 * @param  {Array}           value         The label keys array.
 * @param  {string}          classes       The classes to be added to the table cell.
 * @param  {number}          columnWidth   The width of the column as percentage.
 * @return {HTML}                          The table cell DOM elements.
 */
export function DateContentCell({value, classes, columnWidth}) {

    let formattedDate,
        formattedDateMonth,
        formattedDateWithoutMonth,
        isDateValid = true;

    try {
        formattedDate = dateFormat(new Date(value), "mmm dd, yyyy HH:MM");
    }
    catch (err) {
        formattedDate = <FormattedMessage id="General_CoreFunctionality_UIText_general.notAvailable" />;
        isDateValid = false;
        utils.logError("DateContentCell(): ", "Not a date; Value received: " + value);
    }

    if (isDateValid) {
        formattedDateMonth = formattedDate.split(' ')[0];
        formattedDateWithoutMonth = formattedDate.substring(formattedDate.indexOf(' ') + 1);
    }

    return (
        <td className={classes} style={{width: columnWidth + '%'}}>
            {
                isDateValid ?
                <span>
                    <FormattedMessage
                        id={"General_CoreFunctionality_UIText_date.month." + formattedDateMonth } /> <span>{formattedDateWithoutMonth}</span>
                </span>
                :
                <span>{formattedDate}</span>
            }
        </td>
    );

}

/**
 * Cell that contains PESTLE risk scores.
 *
 * @constructor
 * @param  {string}          classes       The classes to be added to the table cell.
 * @param  {number}          columnWidth   The width of the column as percentage.
 * @return {HTML}                          The table cell DOM elements.
 */
export function PESTLEContentCell({classes, columnWidth, format}) {

    let existingRiskAngles = format ? format() : {};

    let riskAngles = [];

    Object.keys(existingRiskAngles).map(function(value){

        if (existingRiskAngles[value].visible) {
            riskAngles.push((
                <div key={"risk-angle-row-"+value}>
                    <span className="risk-name"><FormattedMessage id={"General_CoreFunctionality_UIText_general." + value}/></span>
                    <span className="risk-bullets">
                            <span className={'risk-bullet high ' + (existingRiskAngles[value].HIGH ? '' : 'hidden')}>
                                <FormattedMessage id="General_CoreFunctionality_UIText_general.risk.H"/>
                            </span>

                            <span className={'risk-bullet medium ' + (existingRiskAngles[value].MEDIUM ? '' : 'hidden')}>
                                <FormattedMessage id="General_CoreFunctionality_UIText_general.risk.M"/>
                            </span>

                            <span className={'risk-bullet low ' + (existingRiskAngles[value].LOW ? '' : 'hidden')}>
                                <FormattedMessage id="General_CoreFunctionality_UIText_general.risk.L"/>
                            </span>
                        </span>
                </div>
            ));
        }
    });

    let pestleCellClasses = classes ? classes : '';

    if (riskAngles.length === 0) {
        return (<td className={pestleCellClasses} style={{width: columnWidth + '%'}}></td>);
    }

    return (
        <td className={'pestle-cell ' + pestleCellClasses} style={{width: columnWidth + '%'}}>
            {riskAngles}
        </td>
    );
}

function TitleCellNoLink(props) {
    let articleTitle = utils.toEllipsis(props.rowData.title, 150);
    let articleSubtitle = utils.toEllipsis(props.rowData.source, 169);
    let articleContent = utils.toEllipsis(props.rowData.content, 170);
    let isNotLicensedContent = props.rowData.licensed ? false : true;

    return (
        <td className={props.classes} style={{width: props.columnWidth + '%'}}>

            {/* It's best to pass the article ID to the click handler in order to avoid issues
                that might occur if it is fetched from a data attribute (e.g.: Google Translate
                generates extra <font> elements inside this <a> tag, thus event.target is not <a>). */}
            <a className="article-title" onClick={ (event) => props.clickHandler(event, props.rowData.articleId.toString(), props.row) }>
                {articleTitle ? articleTitle : props.intl.formatMessage({id: 'General_CoreFunctionality_UIText_general.headlineNotAvailable'}) }
            </a>

            <div className="article-subtitle">
                {articleSubtitle}
                {isNotLicensedContent ? <span className="la-ExternalLink"></span> : null}
            </div>
            <div className="article-content">{articleContent}</div>
        </td>
    );

}
TitleCellNoLink = injectIntl(TitleCellNoLink);
export { TitleCellNoLink };

/**
 * Cell that contains title and text.
 *
 * @constructor
 * @param  {Array}           value         The label keys array.
 * @param  {Array}           labels        The array with all the currently available label texts.
 * @param  {string}          classes       The classes to be added to the table cell.
 * @param  {number}          columnWidth   The width of the column as percentage.
 * @param  {CustomTableBody} table         The reference to the table body component.
 * @return {HTML}                          The table cell DOM elements.
 */
function TitleCellLink({classes, columnWidth, rowData, urlBuilder, intl}) {

    let articleTitle = utils.toEllipsis(rowData.title, 150);
    let articleSubtitle = utils.toEllipsis(rowData.source, 169);
    let articleContent = utils.toEllipsis(rowData.content, 170);
    let articleTitleUrl;
    let isNotLicensedContent = rowData.licensed ? false : true;


    articleTitleUrl = urlBuilder(rowData);
    //create article url for title link
    // rowData.articleId ?
    //     articleTitleUrl = '/#/preview-company/' + companyId + '/' + rowData.articleId + '/' + backRoute :
    //     articleTitleUrl = '/#/preview-company/';

    return (
        <td className={classes} style={{width: columnWidth + '%'}}>
            <a className="article-title"
               href={articleTitleUrl}>{articleTitle ? articleTitle : intl.formatMessage({id: 'General_CoreFunctionality_UIText_general.headlineNotAvailable'})}
            </a>
            <div className="article-subtitle">
                {articleSubtitle}
                {isNotLicensedContent ? <span className="la-ExternalLink"></span> : null}
            </div>
            <div className="article-content">{articleContent}</div>
        </td>
    );

}

TitleCellLink = injectIntl(TitleCellLink);
export { TitleCellLink };

/**
 * Cell that contains language with i18n.
 */
export function LanguageCell({columnWidth, value}) {
    let language = value.toLowerCase();
    return (
        <td style={{width: columnWidth + '%'}}>
            <FormattedMessage id={'General_CoreFunctionality_UIText_languageSelect.' + language} />
        </td>
    );
}

/**
 * Cell that contains experian number and validation icon.
 */
export function ExperianCell({columnWidth, value}) {

    let isExperianValid = value ? 'isValid' : 'isInvalid';

    return (
        <td style={{width: columnWidth + '%'}}>
            <ValidationIcons isVisible={true} renderState={isExperianValid}/>
            &nbsp;
            {
                value ?
                    <FormattedMessage id='General_CoreFunctionality_UIText_general.yes'/>
                    :
                    <FormattedMessage id='General_CoreFunctionality_UIText_general.no'/>
            }
        </td>
    );
}


// WEBPACK FOOTER //
// ./src/scripts/reusable/CustomTableBody/templates/CustomTableBody.predefinedCellTypes.jsx